/**
 * TermsDialog 文案內容（內建，繁體中文為最終解釋版本）
 *
 * 站台相關資訊以 token 內插，由消費端透過 props 傳入：
 *   - {siteName} → 站台/品牌名稱（siteName prop）
 *   - {siteUrl}  → 站台網址（siteUrl prop）
 *
 * 目前僅提供 zh_TW；其餘語系皆 fallback 到 zh_TW（見 getTermsContent）。
 *
 * 結構：
 *   CONTENT[locale] = {
 *     tabs: { privacy, terms },
 *     close,
 *     updatedLabel,
 *     privacy: { title, updatedAt?, sections: [{ heading, body: string[] }] },
 *     terms:   { title, updatedAt?, sections: [{ heading, body: string[] }] },
 *   }
 *   heading 為空字串時不渲染標題（用於前言段落）。
 */

const TOKEN_RE = {
  siteName: /\{siteName\}/g,
  siteUrl: /\{siteUrl\}/g
};

/**
 * 將文字內的 {siteName} / {siteUrl} 換成實際值。
 * @param {string} text
 * @param {{siteName?: string, siteUrl?: string}|string} vars - 傳字串時視為 siteName
 */
export const interpolate = (text, vars = {}) => {
  if (typeof text !== 'string') return text;
  const { siteName = '', siteUrl = '' } =
    typeof vars === 'string' ? { siteName: vars } : vars;
  return text.replace(TOKEN_RE.siteName, siteName).replace(TOKEN_RE.siteUrl, siteUrl);
};

const CONTENT = {
  zh_TW: {
    tabs: { privacy: '隱私條款', terms: '服務條款' },
    close: '關閉',
    updatedLabel: '最後更新日期',

    privacy: {
      title: '隱私政策',
      sections: [
        {
          heading: '',
          body: [
            '這是 {siteName} 擁有者和運營商的隱私政策。',
            '本隱私政策闡述了 {siteName} 將如何管理我們因您成為 {siteName} 會員、使用我們位於 {siteUrl} 的網站以及在「{siteName}」品牌下的任何頁面或網站（包括由我們擁有和/或運營的任何手機或平板電腦應用程序）而收集的個人信息，或我們以其他方式與您打交道時收集的信息。',
            '我們致力於確保我們收集的關於您的個人信息受到保護，並根據《隱私法》和本隱私政策來使用、存儲和披露。'
          ]
        },
        {
          heading: '1. 網站使用',
          body: [
            '在使用本網站時，您同意受本隱私政策條款的約束。',
            '{siteName} 可能會定期審查隱私政策，並保留通過在網站上發布此類更改而隨時自行決定更改隱私政策的權利。',
            '更改將在發布在網站上時立即生效。在發布後繼續使用網站將構成您對這些更改的接受。',
            '本隱私政策不適用於鏈接到本網站且 {siteName} 無法控制的網站。'
          ]
        },
        {
          heading: '2. 個人信息',
          body: [
            '{siteName} 可能從您那裡收集的個人信息類型包括：',
            '● 姓名',
            '● 電子郵件地址',
            '● 個人提交的偏好',
            '● 出生日期',
            '● 國籍',
            '● 實際地址',
            '● 身份證號碼',
            '● 政府頒發的身份證明',
            '● 位置數據',
            '● 設備信息',
            '● IP 地址',
            '公司通過以下方法收集或可能收集此類信息：',
            '● 帳戶註冊過程中輸入的數據',
            '● 與客戶的溝通'
          ]
        },
        {
          heading: '3. 收集目的',
          body: [
            '{siteName} 將僅通過合法和公平的手段收集您的個人信息，不會以侵入性的方式操作其作為持牌在線投注博彩公司的業務，收集目的如下：',
            '● 如果您希望訂閱 {siteName} 的營銷通訊。',
            '● 處理您成為 {siteName} 或任何管理子域會員的申請。',
            '● 為您作為會員提供和改進服務。',
            '● 識別您作為會員的身份並驗證您的身份，以確保安全並遵守我們的法律義務。',
            '● 維護您的會員帳戶。',
            '● 升級和增強您在網站上或通過電話的體驗，或定制或開發信息、服務或產品以滿足您的需求，可能包括市場研究和進行促銷活動。',
            '● 通過電子郵件、短信或電話與您溝通，告訴您我們認為可能對您感興趣的產品或服務。',
            '● 通過人口統計分析、數據庫統計分析創建會員的匯總數據，提供給潛在和現有的 {siteName} 持有者，並允許 {siteName} 的業務更有效率地運作。',
            '● 回應您的問題、評論或請求。',
            '● 確定會員支付商品和服務稅以及其他適用稅款的責任。',
            '● 遵守 {siteName} 的合同、法律和法定義務。',
            '● 如果 {siteName} 有理由懷疑與我們的功能和活動相關的非法活動或嚴重不當行為已經、正在或可能發生，採取適當行動。',
            '● 建立、行使或辯護任何法律索賠。',
            '● 管理工作申請。',
            '如果您不向 {siteName} 提供我們要求的個人信息，我們可能無法處理您成為會員的申請，為您提供我們的服務或回應您的查詢或投訴。',
            '通過使用 {siteName}，成為會員或以其他方式選擇向 {siteName} 提供個人信息，您同意 {siteName} 收集、使用、處理並可能披露您的個人信息，用於上述目的，並基於本隱私政策中描述的基礎。'
          ]
        },
        {
          heading: '4. 直接營銷和退出',
          body: [
            '我們可能會不時使用您的個人信息，告知您有關我們的產品或服務或 {siteName} 認為可能對您有興趣或有益的促銷活動。我們可能會通過電子郵件、短信、電話或郵件這樣做。如果您不再希望收到 {siteName} 的任何或任何特定形式的營銷或促銷材料，您可以隨時通過在線客服聯繫我們。',
            '我們將在實際可行的情況下盡快遵守您的請求。',
            '我們可能會不時就您的 {siteName} 帳戶的管理和行政事務與您聯繫。這些通訊可以通過註冊為會員時記錄的任何聯繫方式進行。此類通訊不會影響您對直接營銷通訊的選擇加入或選擇退出狀態。'
          ]
        },
        {
          heading: '5. 個人信息管理',
          body: [
            '{siteName} 將採取一切合理步驟確保其收集、使用或披露的個人信息是正確的，並存儲在只有授權人員才能訪問的安全環境中。我們按照本隱私政策中描述的方式存儲我們收到的個人信息，存儲時間為您使用我們的服務期間，或根據需要履行收集目的、提供我們的服務、解決爭議、建立法律辯護、進行審計、追求合法商業目的、執行我們的協議以及遵守適用法律所需的時間。',
            '當個人信息不再需要用於 APPs 下允許的任何目的（包括我們的法律或運營義務）時，{siteName} 將銷毀或永久去標識我們持有的個人信息。'
          ]
        },
        {
          heading: '6. 個人信息安全',
          body: [
            '您承認通過互聯網傳輸的數據不是完全安全的。因此，{siteName} 不保證或擔保您傳輸給它的任何信息的安全性。您傳輸給 {siteName} 的任何信息均由您自行承擔風險。但是，一旦 {siteName} 收到您的傳輸，{siteName} 將採取合理措施保護您的個人信息免受濫用、丟失和未經授權的訪問、修改和披露，包括使用密碼保護系統和數據庫以及安全套接層（SSL）技術。通過使用我們的服務或向我們提供個人信息，您同意我們可以通過電子方式與您溝通有關您使用服務的安全、隱私和行政問題。',
            '{siteName} 的員工、代理和承包商必須維護會員個人信息和交易行為的機密性。發布在公告板上或在社交媒體環境中通信的信息（例如，Facebook、Twitter、聊天室）成為公共信息。{siteName} 不能保證這種披露信息的安全性。',
            '我們認真負起責任，不讓兒童使用我們的服務。我們不會接受他們的信息用於開設投注帳戶。然而，最終監控子女互聯網活動的責任在於父母或監護人，包括在適當情況下使用互聯網篩選軟件。',
            '請記住，在完成網站上的時間後，始終登出您的帳戶。如果您與他人共用一台計算機，這一點尤為重要。您負責自己的計算機、移動設備或用於訪問網站的任何其他手持設備的安全和訪問。',
            '最終，您單獨負責維護您的用戶名、密碼和任何帳戶信息的保密性。請在使用互聯網和我們的網站時保持謹慎。'
          ]
        },
        {
          heading: '7. 訪問個人信息',
          body: [
            '您可以通過點擊網站上的「設置」鏈接或通過在線客服聯繫我們來訪問 {siteName} 收集的個人信息。',
            '如果可能且在合理期限內，我們將按照您要求的方式讓您訪問您的個人信息。如果我們拒絕您的請求或無法以您要求的方式讓您訪問，我們將盡力通過其他方式滿足您的要求。在某些情況下，我們可能不需要讓您訪問您的個人信息，這些情況在 APPs 中有所規定，包括可能對另一個人的隱私產生不合理影響的情況。如果我們因此類原因拒絕訪問，我們將以書面形式通知您拒絕的理由以及您可用的投訴機制。'
          ]
        },
        {
          heading: '8. 刪除個人數據',
          body: [
            '如果 {siteName} 不再有合法理由繼續處理或存儲您的個人數據，您可以要求刪除它。請注意，這項權利不是有保障的——意思是如果 {siteName} 因法律義務而必須存儲您的數據，它將無法遵守您的請求。您可以通過聯繫我們的在線客服請求刪除您的個人數據。'
          ]
        },
        {
          heading: '9. 聯繫方式',
          body: [
            '如果您對個人信息的處理有任何疑問、訪問或更正請求或投訴，請通過網站客戶支持聊天功能與我們聯繫。'
          ]
        },
        {
          heading: '10. 國際數據傳輸',
          body: [
            '我們處理的所有信息可能會在世界任何地方傳輸、處理和存儲，包括但不限於其他國家，這些國家可能有與您居住地法律不同的數據保護法律。',
            '我們努力保護您的信息，符合適用法律的要求。'
          ]
        },
        {
          heading: '11. 處理的法律基礎',
          body: [
            '就歐盟《一般數據保護條例》而言，{siteName} 處理您個人信息的法律基礎如下：',
            '履行合同：{siteName} 需要處理您的個人信息以履行我們與您或我們的客戶的合同。',
            '合法利益：{siteName} 處理您的個人信息以進一步實現我們的合法利益，但僅限於我們的利益不被您的利益或基本權利和自由所凌駕的情況。',
            '同意：在某些情況下，{siteName} 還依賴您的同意來處理您的個人信息。',
            '遵守法律義務：{siteName} 可能會處理您的個人信息以遵守我們的法律義務。'
          ]
        },
        {
          heading: '12. 監管機構',
          body: [
            '如果您位於歐洲經濟區，如果您認為我們對您個人信息的處理違反了適用法律，您有權向監管機構提出投訴。'
          ]
        },
        {
          heading: '13. 本隱私政策的更新',
          body: [
            '如前所述，{siteName} 可能會不時審查、更改和更新本隱私政策，以反映我們當前的做法和義務。',
            '我們將在我們的網站 {siteUrl} 上發布我們當前的隱私政策，更改將在發布時生效。',
            '您應定期查看此隱私政策並保持熟悉其條款。'
          ]
        }
      ]
    },

    terms: {
      title: '服務條款',
      sections: [
        {
          heading: '',
          body: [
            '請仔細閱讀以下的規則和條款（「條款」）。通過註冊帳戶（「用戶帳戶」或「帳戶」），用戶默認接受這些條款。',
            '這些條款適用於 {siteName} 的所有產品和服務（「服務」）、網站（「網站」）以及與服務（「投注」）相關的所有交易。這些條款和條件、規則和法規以及隱私政策均以繁體中文撰寫。如果這些條款和條件、規則和法規以及隱私政策被翻譯成其他語言，除繁體中文外，繁體中文版本將被視為最終解釋。'
          ]
        },
        {
          heading: '1. 規則和條款簡介',
          body: ['1.1 本公司是該網站（{siteUrl}）的運營商，提供線上體育博彩和遊戲服務。']
        },
        {
          heading: '2. 合作夥伴',
          body: ['2.1 雙方必須遵守以下規則和條款（「本公司」或「公司」在條款中）。']
        },
        {
          heading: '3. 條款的範圍和條件',
          body: [
            '3.1 只要用戶登入網站使用本公司的服務，和/或在本公司開設帳戶，和/或在本公司進行投注，本公司將被視為已經閱讀、理解並接受了這些條款和條件。在同意這些條款和條件以及用戶繼續使用本網站的情況下，用戶將受到由本公司章程和隱私政策管理的條款和條件的約束。如果這些條款和條件和所包含的參考之間存在任何不一致，則這些條款和條件將始終作為最終參考。',
            '3.2 這些條款將於 2017 年 7 月 1 日生效。我們可能需要不時對條款進行更改，原因包括（但不限於）商業原因、遵守法律法規，或基於客戶服務原因遵循監管機構的指導或建議。您可以點擊網站頁腳部分的《使用條款》鏈接查看最新版本的條款。生效日期將在 3.2 的開頭指明。',
            '3.3 如果需要更改條款，本公司將儘快提前通知用戶。通知方法如下：對於次要和不重要的更改，本公司將不會通知，因此建議用戶定期閱讀規則和條款。在條款更改後繼續使用網站和服務將被視為遵守默認條款。',
            '3.4 本公司將採用謹慎和合理的方式通知用戶需要提醒的條款更改，包括：電子郵件地址（用戶提供的電子郵件地址）；用戶網站上的帳戶；網站上的通知，即在新條款的復選框中點擊「是」或「接受」。只要用戶承認通知，或在通知後繼續使用網站或服務，用戶將被視為接受並受條款約束，包括（為了澄清疑義）無論用戶是否已閱讀已更改的條款。如果用戶不接受已更改的條款，用戶可以停止使用服務或關閉用戶的帳戶。',
            '3.5 根據第 3 條和第 7 條所提到的資金條款的更改，本公司將以謹慎和合理的方式提前通知用戶，用戶需要在新條款的復選框中點擊「是」或「接受」以確認更改。只要用戶確認通知，用戶將接受這些條款並受其約束。如果用戶不接受已更改的條款，用戶可以停止使用服務或關閉用戶的帳戶。'
          ]
        },
        {
          heading: '4. 帳戶開立和維護',
          body: [
            '4.1 如果用戶想在線下注，用戶需要註冊並持有用戶帳戶。',
            '4.2 用戶同意提供與註冊公司有關的所有相關個人數據，用戶有責任確保用戶的個人信息在網站上保持最新，特別是地址、電話號碼和支付/銀行詳細信息（如果適用）。用戶必須如實提供，當帳戶開立時，不得違反這些條款和條件，否則將導致帳戶中的所有可用資金被沒收。用戶同意提供任何文件以供公司驗證用戶提供的個人信息的真實性。用戶授權公司使用公司認為必要的手段來驗證用戶提供的個人信息，以線上註冊形式（合法手段「個人信息」）。這些個人數據包括用戶根據公司的絕對酌情裁量需要提供的所有信息，以使公司能夠正確識別用戶。當本網站上的上述註冊表單被提交並被公司接受時，用戶將被視為註冊客戶（「註冊客戶」）。公司保留出於任何原因接受或拒絕註冊的權利。只有註冊用戶才能使用公司的服務，其投注金額不得超過自己設定的最高限額，且投注金額不得超過其帳戶的總金額。',
            '4.3 要開設帳戶，用戶必須保證：',
            '4.3.1 如果用戶希望與本公司下注，用戶應注意，用戶的國家、居住地禁止在線賭博和在線投注和/或在線賭博（統稱為「賭博」），或禁止訪問本網站。用戶必須不可撤銷且無條件保證上述情況不存在，否則用戶將無法在任何時候訪問或註冊本網站：',
            '（1）用戶所在國家管轄範圍內禁止使用或以任何理由使用本網站；',
            '（2）用戶所在國家管轄範圍內禁止賭博；',
            '（3）如果使用者所居住的地區禁止其居民參與網路博彩；同時',
            '（4）如果用戶居住在以下任何一個國家或者行政轄區：香港特別行政區、澳門特別行政區、美國以及所屬領地、以色列、英國、法國、西班牙、葡萄牙、瑞士、愛爾蘭、比利時、賽普勒斯、土耳其、臺灣、新加坡、菲律賓共和國（統稱「禁止轄區」）。',
            '可以使用此網站或者服務的國家或行政轄區不包括本公司提供、邀請或懇請使用網站但本身禁止博彩的國家或行政轄區。用戶同意，用戶使用本網站風險自負，並進一步瞭解並同意，使用本網站和/或提供的服務外，使用者可能會因為投注而失去金錢，用戶接受任何這類損失承擔全部責任。',
            '4.3.3 (a) 用戶至少年滿 18 歲，或 (b) 在使用者所在區域參加體育博彩為合法行為的管轄區內，以較高者為准（「相關年齡」）。',
            '4.3.4 用戶未被禁止參與博彩活動。',
            '4.3.5 用戶能夠簽訂具有法律約束力的合約，包括這些規則和條款以及使用者使用公司服務的任何條款。',
            '4.3.6 用戶未曾有過被關閉的帳戶。',
            '4.4 用戶只允許在本網站開設一個帳戶，本公司保留唯一及絕對酌情權對任何用戶開設多個帳戶查證，本公司將關閉其重複註冊帳戶，或將其重複帳戶餘額推還用戶，或將這些用戶合併。用戶需同意本公司將以其視為合理的方法處理重複註冊帳戶。',
            '4.5 為了保持一個高水準的安全級別以保護客戶資金，公司可能會執行任意安全檢查。用戶在此同意本公司有權要求使用者提供額外的資訊和/或從使用者的檔，以驗證用戶作為帳戶持有人的身份。',
            '4.6 用戶應該在每次登陸網站時檢查帳戶餘額。在用戶的帳戶餘額在任何問題時，用戶應立即通知本公司，並提供本公司上次驗證用戶的帳戶餘額日期與使用者的交易記錄。如果公司在上述日期的 7 天內沒任何在使用者的帳戶餘額記錄，即表示使用者同意放棄任何與使用者帳戶中的任何不符的追索責任，並接受使用者帳戶的所有資訊。'
          ]
        },
        {
          heading: '5. 身份驗證/反洗錢要求',
          body: [
            '5.1 用戶保證：',
            '5.1.1 開戶的名字和地址都準確無誤。',
            '5.1.2 用戶為帳戶存入款項的合法所有人。',
            '5.2 同意以上條款之後，用戶授權本公司時不時進行身份驗證，這是本公司也是協力廠商（包括但不限於監管部門）驗證資訊（驗證）的要求。因此，使用者須不時的提供已提供資訊的更多詳情，包括帳戶存款方面資訊。',
            '5.3 公司進行資訊驗證時，會限制用戶的取款要求以及網站使用以及其他服務。由於監管、安全以及其他商務原因，公司會進行資訊的重複驗證。如果用戶有任何疑問，請聯繫客服團隊。',
            '5.4 在某些情況下，公司可能會聯繫用戶並要求提供更詳細的資訊以便公司完成驗證。為此，公司將行使絕對唯一的權力要求用戶提供經過公證的身份證件或任何具有法律效力的身份證明，及其它位址證明、水電費帳單、銀行資料、銀行對帳單及銀行資信證明。直至使用者提供的資訊達到驗證要求，公司將阻止或授權使用者進行任何與帳戶相關的活動。如果公司確認使用者所提供資訊為不真實資訊，公司將有權沒收用戶的帳戶款項並關閉其相關帳戶。',
            '5.5 法定年齡之下的用戶將無法使用網站及其服務。公司如果無法確定用戶的法定年齡，用戶的帳戶將會被暫停使用直到年齡確定為止。如果公司確定用戶沒有達到博彩的法定年齡：',
            '5.5.1 用戶的帳戶將會被凍結。',
            '5.5.2 所有交易都將無效，相關的存款資金將根據資金存款（如果適用）的支付方式退還。',
            '5.5.3 法定年齡下的存款將全部退還。',
            '5.5.4 法定年齡下所贏得的獎金將被沒收（或是從 5.5.3 中所退還的存款中扣除），同時使用者必須歸還從帳戶中提取的資金。'
          ]
        },
        {
          heading: '6. 用戶名、密碼、PIN，以及客戶資訊',
          body: [
            '6.1 作為註冊客戶，使用者將使用一個用戶名和密碼（帳戶查詢資訊），本帳戶僅限用戶個人使用，用戶需承擔在任何時候確保該帳戶存取資料的保密性的責任。用戶應充分對任何濫用和/或未經授權的訪問帳號資訊披露給任何協力廠商的行為負責。如果使用者擔心使用者的帳戶查詢資訊已對外公佈，其安全受到侵害或有受到任何協力廠商訪問時，用戶應該立即通知本公司，本公司將立即查詢帳戶的資訊。只有在用戶通知用戶的帳戶存取訊號已被破壞，而本公司將暫停該帳戶投注或要求公司作出的投注和請求的網上帳戶被視為無效。',
            '6.2 本公司可能需要使用者更改使用者的密碼或使用者的帳戶查詢資訊，否則本公司可暫停用戶的帳戶，如本公司有理由相信，可能出現安全網站的濫用行為。本公司可保留其獨有及絕對酌情權，在通知用戶之前，修改帳戶查詢資訊。',
            '6.3 若帳戶密碼因故遺失，使用者可向公司客服人員申請取回密碼。申請取回密碼時需提供正確的註冊資料，包括帳號、電話號碼等。若資料不正確導致無法核實，將被沒收所有權益。'
          ]
        },
        {
          heading: '7. 帳戶資金',
          body: [
            '7.1 用戶只能通過在本網站上發佈、被預先核准的付款方式向本公司存款，使用者必須只通過相同的付款解決方案，由公司授權的公司（授權支付資金解決方案）存款和收款，除非用戶的錢直接存入本公司。授權支付解決方案公司在未經本公司事先書面聲明之前，本公司對資金並進一步授權支付解決方案不能以任何方式市場、廣告、宣傳，或推廣本公司或本公司服務。存取款以及存款的高低限額詳情，用戶可以參考網站的支付方式頁面。',
            '7.2 支付方式中如果使用者非帳戶持有者，在條款 5.2 的所有驗證完成之後，使用者所有的存款將視為無效（所有的獎金也視為無效）。',
            '7.3 所有投注都必須有足夠的資金支持，公司有權視無足夠資金支持的投注無效。',
            '7.4 如果用戶拒付、拆出資金或其他付費，公司有權收取用戶相關款項。為避免疑問，用戶的帳戶不可用作銀行帳戶，公司相應的不能因投注而從帳戶中存取款項，但公司有權從中扣減行政費用（不管有無關閉或暫停帳戶）。存款資金不收取利息。',
            '7.5 因協力廠商交易成本原因，公司有權根據所選擇的支付方式收取適當的存款和取款手續費。相關情況，公司將予以通知。',
            '7.6 應用戶當地或稅收或其他當局要求，用戶有責任告知其獲得的獎金或損失額度。',
            '7.7 所有已投注的獎金都將存入用戶帳戶。如果存款過程有誤，使用者應立即告知公司。使用失誤獎金進行的投注均視為無效投注，帳戶調整將恢復此類資金。',
            '7.8 用戶需保持積極使用本公司帳戶。用戶必須在任何 12 個月內最少一次登錄和使用本網站，以保持一個活躍帳戶。如果用戶的帳戶是連續 12 個月或更長時間不活動，本公司保留權利關閉此帳戶，同時用戶也同意放棄帳戶餘額以及對本公司追究責任。',
            '7.9 公司將每年審查顧客資料庫，查看是否有未託售的「不活躍」帳戶。如果帳戶被定為不活躍帳戶，公司將通過電話、郵件或是書信根據使用者所提供的資訊聯繫使用者。如果帳戶被定為「不活躍」帳戶之後的 12 個月內仍然無法和用戶取得聯繫，未償付的資金將被駁回，同時關閉帳戶並將資金分發給公司指定的慈善團體。'
          ]
        },
        {
          heading: '8. 投注接受和服務使用',
          body: [
            '8.1 本公司只接受註冊用戶通過網站投注。投注以及服務的使用可以參考説明選項下的說明。投注詳情、使用網站進行的賭注以及相似交易（交易）須嚴格遵守相關的投注規則和遊戲規則。',
            '8.2 註冊用戶只能通過本網站投注，用戶的投注被認為是從其所在轄區的互聯網協定位址所記錄。只有本公司所在的伺服器接收並確認投注，這一次投注才算被接受。當用戶已成功地在網站上下注，使用者將收到電子確認（通知書），這是本公司接受和確認投注的記錄。當投注被接受並成功記錄之後，本網站會通知使用者該資訊。當本公司在該轄區的伺服器接收並記錄某次投注時，而該投注遵守以上的條款和條件時，該投注才被視為成功。',
            '8.3 如果某次投注的傳輸未完成，該次投注將被視為無效，包括但不限於當投注由於技術問題，傳輸受到干擾或中斷。',
            '8.4 用戶不得取消或更改已經由本公司確認、接受和記錄的投注。如果用戶在投注時出現任何爭論，請用戶在投注被確認前通知本公司。本公司將保留唯一和合理的酌情權對上述的投注進行調查。',
            '8.5 本公司保留權利隨時暫停或禁止進一步的投注行為。當某個投注項目被暫停或禁止訪問時，任何試圖進入投注的請求都將被拒絕。',
            '8.6 如公司有確實證據，證明繼續使用帳戶會對用戶或本公司造成任何形式的傷害或損失；或是處在違反條款、規則條例，或隱私政策期間；或是公司確認用戶違反這些條款或者用戶提出控訴。本公司保留享有唯一的絕對酌情權拒絕任何投注或任何未經說明的部分投注；或者在任何時間任何原因暫停或關閉帳戶。',
            '8.7 本公司不承擔任何因為設備或電信通信因素所造成的投注確認、接受或記錄的責任。',
            '8.8 本公司不承擔因為網站內容的缺漏而造成的損失，包括但不限於延誤操作或傳輸中斷，通信線路故障，任何人使用任何錯誤或遺漏的損失。',
            '8.9 投注將在接受投注限期完成之後被接受。投注限期遵循條款和條件，用戶也必須遵守之。如果投注在投注限期之後被接受，本公司保留權利，視本次的投注無效。',
            '8.10 通過信用卡或借記卡進行的投注，在本公司沒有收到全額款項之前，投注不被認為生效直到本公司（或其授權支付解決方案）已收到有效的足額款項為止，根據第 8.2 段。在使用者已經把全部投注付清之前，該投注將自動作廢。',
            '8.11 用戶的帳戶必須有足夠的資金餘額，以支付用戶能夠投注在投注數額。否則投注將不會被允許。',
            '8.12 所有在網站上顯示的賠率會有所變化，但當投注確認之後，該投注對應的賠率將被鎖定，並以以上條款和條件第 8.2 條為准。本公司保留權利，隨時更改賠率、價格以及任何的投注資訊，恕不另行通知。同樣本公司保留自行決定的權利，以糾正任何時候受任何影響而產生的錯誤、遺漏或錯誤不正確的可能性。如出現這種情況，本公司將在網站上放置突出的通知。',
            '8.13 不同的市場投注類型伴隨最高的投注金額，本公司保留唯一及絕對酌情權在未通知用戶之前對其進行修改。'
          ]
        },
        {
          heading: '9. 共謀、作弊、欺詐以及犯罪行為',
          body: [
            '9.1 以下和網站相關的行為事項（任何一項）：',
            '· 濫用獎金以及其他優惠資金；',
            '· 使用不公平的外部因素和影響（簡稱作弊）；',
            '· 獲取不公平的利益（條款 9.5.3）；',
            '· 開設重複帳戶；',
            '· 從事欺詐和犯罪行為（條款 9.5）。',
            '以上皆為「禁止事項」，用戶須嚴格遵守，否則視為違反條款。公司將竭盡全力監督並遏制以上行為，用戶一旦違反條款，公司會立即找出相關違反者。據此造成的損失，公司將不負任何責任，並有最終裁決權。',
            '9.2 用戶一旦發現任何違反禁止事項的玩家，應立即通過網站向客服團隊舉報。',
            '9.3 使用者使用網站和服務過程中禁止參與任何有關禁止事項的行為。',
            '9.4 如果公司有足夠證據證明用戶參與了任何禁止事項（包括對公司（以及合作夥伴或是其他方）在某些時間在博彩以及博彩行業欺詐作弊以及共謀的行為；或是在其他的博彩公司線上遊戲投注時候被發現違反禁止事項或是其他不適當行為；或是在帳戶中曾經「拒絕付款」或是否認購買和存款的行為；或是用戶持續使用對危害監管的行為，包括 CEZA 的許可效力或是用戶已破產或是在其他地方受到訴訟的行為），那麼（包括可能暫停或終止帳戶），公司有權對其帳戶（或是用戶持有的受到公司經營管理的帳戶）沒收所有或是部分餘額或是收回受到影響的或是與條款 10.4 相關的存款、付款、紅利以及獎金。9.4 提及的權力不受之前提到的其他權力（包括一般的法律權力）的干擾，不管是在這些條款還是其他條款的條件之下。',
            '9.5 對於條款 9：',
            '9.5.1 欺詐行為 是指任何用戶或是代表用戶或與用戶共同參與謀劃的任何欺詐活動的行為，包括但不限於：(a) 欺詐退款行為；(b) 在任何時候由用戶或其他任何合謀參與同一遊戲者使用被偷竊的、被複製的或其它未經授權的信用卡、借記卡作為資金來源；(c) 用戶與他人勾結以獲取不公平利益（包括公司提供的獎金或類似激勵獎金）；(d) 任何企圖註冊虛假或誤導性帳戶資訊；(e) 用戶任何實際或未遂的，惡意製造，或騙取公司或規避任何合同或法律限制的行為，無論是否實際上對公司造成損害或傷害，但公司有理由認為其在任何合理的司法管轄權內非法。',
            '9.5.2 犯罪行為 包括但不限於，洗錢及任何違反 CEZA 互動式博彩制度和法規 V2.0/08/04 的行為。',
            '9.5.3 不公平優勢 應包括但不限於：使用者利用公司或任何與協力廠商相關的軟體服務（包括涉及的任何遊戲）的錯誤、漏洞、失誤；技巧遊戲中或任何其他可能的禁止行為中使用 Bots；使用協力廠商軟體或分析系統；利用的條款 12 定義的失誤，此失誤對用戶有利或對本公司或其他方無利。',
            '9.6 公司全力保證，在行使條款 9.4 中與「禁止事項」相關的任何權利時，須遵守相關管理和法律規定，並且對使用者和其他客戶都公平正義。',
            '9.7 公司有權告知如下部門（包括相關部門、其他線上遊戲或者博彩運營商、其他線上服務提供者和銀行、信用卡公司、電子支付服務提供者或其他金融機構）用戶身份以及任何用戶曾經涉嫌違反「禁止事項」的行為，用戶要完全配合公司的調查。'
          ]
        },
        {
          heading: '10. 關閉帳戶：終止規則和條款',
          body: [
            '（使用者關閉帳戶終止協議）',
            '10.1 用戶可從其帳戶託售，前提是用戶的帳戶有足夠的餘額並且託售已被本公司通過，用戶在託售前需參考網站所提供的託售指示。',
            '10.2 用戶可以取消其帳戶，請聯繫我們。如果用戶決定取消本公司帳戶，用戶必須立即停止使用本網站。只有經本公司確認已經被取消的帳戶，所作出的投注或請求被視為無效，用戶將繼續為用戶的帳戶活動的任何責任，直至取消戶口從上述通知本公司。',
            '10.3 在條款 10 下關閉帳戶後，公司有權（不受條款 10.5 權力限制）沒收違反以下行為的資金：（a）依照條款 9（共謀、作弊、欺詐和犯罪行為）；(b) 依照條款 17（違反規則和條款行為）；（c）其他條款（包括如 5.4）；（d）法律法規。',
            '（公司關閉帳戶終止協議）',
            '10.4 償還未清餘額時，公司將使用用戶註冊帳戶時的付款方式，或是其他合理付款方式。',
            '10.5 公司有絕對唯一酌情權根據聯繫方式書面通知（或具體通知）使用者關閉帳戶以及終止協議（儘管其他的條款包含了此規則和條款）。根據條款 10，終止協定之前，公司將儘快退還用戶的剩餘款項。',
            '10.6 關於條款 9（共謀、作弊、欺詐和犯罪行為）或者條款 17（違反規則條款），公司關閉使用者帳戶並終止協議後，使用者帳戶餘額將無法退回且被公司沒收。如果未下投注不違反任何條款，帳戶的關閉和條款的中止不會影響未下投注，條款 9 和條款 17 除外。避免疑問，帳戶關閉後（不管是應用戶要求還是條款要求），公司將不再發放任何獎金，用戶也無權收到任何可能的獎金。',
            '（暫停帳戶）',
            '10.7 公司有絕對唯一酌情權根據條款所述視情況暫停使用者帳戶。帳戶暫停之後：(a) 禁止任何帳戶活動（包括存款、取款、投注或博彩）直到帳戶重新啟動為止；（b）獎金和紅利將不再發放到用戶的帳戶；（c）帳戶暫停後，公司將儘快解決因此而產生的問題，以便帳戶可以重新啟動或關閉。'
          ]
        },
        {
          heading: '11. 協力廠商供應商',
          body: [
            '11.1 公司接受來自協力廠商供應商（包括體育、撲克以及娛樂場）的資訊和服務。通過協力廠商供應商提供的系統，使用者有時無法查看公司掌控的帳戶歷史和交易資訊，若使用者欲瞭解相關資訊，請聯繫公司客服。',
            '11.2 公司欲使用公司服務，必須在存取設備（軟體）中下載並安裝協力廠商提供的軟體。軟體包括但是不限於：存取設備應用程式以及任何其他優惠或行銷的應用、服務以及服務，使用者須與所有者或是許可方在協力廠商在軟體（協力廠商軟體協定）中達成協議。如果條款和協力廠商軟體協定相衝突，將以公司條款為准。',
            '11.3 公司不允許任何員工以及任何人以任何方式和軟體員工或任何與服務供應商（由公司絕對酌情決定）的員工獲取聯繫，從而在供應商提供服務的盤口或事件上投注。否則，一切與之相關的投注將視為無效，公司有絕對唯一的決定權。',
            '11.4 使用者承認公司的伺服器亂數產生程式將決定遊戲的結果，且用戶接受所有遊戲結果。使用者還認可，如果螢幕的結果和公司遊戲伺服器結果存在分歧，將以遊戲伺服器結果為准，使用者認可公司的記錄作為相關線上遊戲活動以及結果的條款和條件的最終權威資料。',
            '11.5 協力廠商連結僅供使用者便利。如果使用者訪問協力廠商連結，使用者將離開主頁面並接受不同網站的使用條款。公司運營的其他網站，提供並非適合任何人的資訊，使用者點擊此類網站必須閱讀並接受相關的使用條款。'
          ]
        },
        {
          heading: '12. IT 故障 / 失誤',
          body: [
            '12.1 如公司提供服務的軟體和硬體出現任何問題，公司會採取所有合理措施儘快解決問題。如果問題導致了遊戲中斷，而又無法毫無損害地完全恢復成遊戲原樣，公司會採取一切措施給予用戶公平答覆（包括恢復用戶帳戶的餘額到最後一次投注或問題發生前的最後一次遊戲登錄時）。',
            '12.2 很多情況下，使用者的投注以及付款會發生失誤，包括以下情況：',
            '12.2.1 由於資訊輸入、盤口設置的明顯失誤或疏忽或是電腦故障，公司謊報賠率或投注條款或博彩投注。',
            '12.2.2 公司的「明顯失誤」。明顯失誤表現為：',
            '12.2.3 開賽前的投注盤口和一般盤口明顯不同；',
            '12.2.4 賽事開始時，提供的投注盤口明顯不正確；',
            '12.2.5 公司繼續接受已經停止盤口的投注，包括正在進行（「正在進行」的已接受的投注除外）賽事或是已經完成的（也稱作「最近投注」）賽事；',
            '12.2.6 由於條款 9.1 的禁止事項而引起的失誤；',
            '12.2.7 公司不應當接受或是有權取消或是重置遵守投注規則（例如和「相關意外事件」）的投注；',
            '12.2.8 由於人工或是電腦失誤，公司在獎金和退款數額的失誤；',
            '12.2.9 公司在免費投注和發放獎金上的失誤；以上情況都算作「失誤」。',
            '12.3 對於失誤，公司有權：',
            '12.3.1 糾正失誤投注以及重置當時投注時所使用或應使用（公佈失誤除外）的正確賠率，且投注默認在當時賠率下已經下注；',
            '12.3.2 在 12.3.1 條款下無法糾正和重置的賠率，宣佈投注無效且歸還用戶投注額；',
            '12.3.3 由於「禁止事項」引起的失誤，採取條款 9.4 的措施。',
            '12.4 發放到帳戶的資金、由於失誤而支付給用戶的資金、在條款 12.3 中滯留的資金或是公司賒欠給用戶的資金，在公司要求付款時，須立即歸還公司。如果發生以上情況，同時使用者帳戶中資金充足，公司有權從資金中扣取款項。公司將竭力找出所有失誤並在糾正之後儘快通知用戶。',
            '12.5 公司（以及雇員和代理）以及合作方和供應方對任何損失，包括用於公司失誤或用戶失誤造成獎金損失都不負責任。',
            '12.6 用戶一旦覺察任何失誤，應該立即通知公司。',
            '12.7 發放到用戶帳戶的且已被使用的資金或是由於投注失誤而發放給用戶的資金，公司有權沒收任何此類資金；如果公司已經支付詞類投注博彩賠率款項，則視此款項為賒欠款，公司有權在用戶帳戶充裕時扣除款項，或用戶應在公司提出要求時立即償還款項。'
          ]
        },
        {
          heading: '13. 不可抗力事件',
          body: [
            '13.1 在不影響各種的司法管轄的法律法規的情況下，對於任何由於不可抗力的事件而造成的損失或損害，包括（但不限於）電源故障；貿易或勞動爭議；任何政府或權威機構的失誤或遺漏；電信服務的不暢和故障；或任何協力廠商和不可抗力因素造成的延誤或故障，公司有權取消或暫停服務且不承擔任何責任。',
            '13.2 任何設備或軟體，不管處在任何地方或是在何處使用，不管是否公司直接控制以及如何引起故障，對於影響到服務的使用以及和公司聯繫的故障，公司一概不負責。',
            '13.3 對於協力廠商未能履行合同規定造成的損失，公司一概不負責。'
          ]
        },
        {
          heading: '14. 病毒、駭客以及其他攻擊',
          body: [
            '14.1 用戶不得攻擊、侵入、未經授權更改或是引入任何惡意程式碼至網站和服務中，包括但是不限於：',
            '14.1.1 反向工程或是反編譯（全部或部分）網上任何軟體；',
            '14.1.2 複製、修改、再生、傳播、更改、使用或播散全部或任何部分服務或網站或包含任何材料或資訊的服務或網站；',
            '14.1.3 向協力廠商揭露任何帳戶資訊；或違反任何以上的規定；',
            '14.1.4 通過使用或滥用服務或網站作弊、欺骗、欺诈、误传或任何方式的诈骗；',
            '14.1.5 通過拒絕服務的攻擊或散佈拒絕服務的攻擊來攻擊網站。公司將向有關執法部門舉報任何涉嫌違反《電腦濫用法》1990 的行為，同時公司將配合當局的揭示用戶身份。在違反相關條款時，公司將立即停止用戶使用該網站和/或服務。',
            '14.2 公司若懷疑用戶沒有遵守本條規定，公司有權（任何其他補救措施除外）凍結或暫停使用者帳戶，並進行進一步的調查。公司如果確認用戶未能遵守本條規定，公司將終止對使用者服務，關閉其帳戶並沒收帳戶餘額。'
          ]
        },
        {
          heading: '15. 索賠和糾紛',
          body: [
            '15.1 如果用戶被發現參與欺詐、不誠實或犯罪行為，公司將尋求刑事和合同制裁，並拒絕付款給用戶。因直接或間接的欺詐、不誠實或犯罪行為造成的所有成本、費用或損失（包括任何直接、間接或從屬損失，利潤損失和名譽損失），用戶應按要求並有責任賠償。',
            '15.2 公司如有證據證明發生如下事件，公司有權拒絕付款並聲明投注無效：(i) 賽事的完整性已被質疑；(ii) 賠率或獎池被人操縱；(iii) 非法操縱。公司可通過任何或所有的投注管道中投注的大小、多少或模式來得出依據。有關管理機構、許可機構和/或任何相關行業協會（如果有）將有最終決定權。',
            '15.3 一旦投注已下定、被接受並記錄，使用者將不允許取消或更改投注，公司沒有義務取消遵循規則和條款的有效、被接受和記錄的投注。如對投注有任何爭議，用戶應在投注接受前和/或賽事發生前已下的賭注告知公司。公司有唯一的絕對和合理裁量權調查並合理解決此類爭議。'
          ]
        },
        {
          heading: '16. 抱怨',
          body: [
            '16.1 用戶如對公司有任何投訴，請聯繫公司客服，客服團隊將儘快給用戶答覆。',
            '16.2 如果用戶對客服的決定不滿意，用戶可以升級投注。為了升級投訴，用戶須要清楚地陳述不滿之前的決定的理由以及其他任何相關資訊。客服主管的決定（如公司認為合理的情況下已完成升級）將被視為最終決定。',
            '16.3 公司如與用戶聯繫，公司擁有唯一絕對裁量權使用任何使用者的聯繫資訊。電子郵件發送之後或公司已經與使用者通過電話直接溝通（包括留言）後或信件寄出三天之後，通知將被視為已正確送達。任何服務的通知證明，且確鑿證明情況必須，比如信件必須寫明確切地址、貼上郵票並投入郵箱；電子郵件則必須發送到指定使用者的電子郵寄地址（如果有）。'
          ]
        },
        {
          heading: '17. 違反規則和條款',
          body: [
            '17.1 如果使用者違反任何規則和條款、管理規定以及隱私政策，公司可以隨時暫停或取消使用者帳戶。如果使用者確實違反了規則和條款，公司擁有唯一絕對裁量權維護公司權利並要求用戶做出補償。另外，公司有權保留任何未償付的資金作為用戶義務擔保和/或因違反這些條款產生的任何責任。'
          ]
        },
        {
          heading: '18. 使用者個人資訊',
          body: [
            '18.1 使用者的帳戶資訊將安全存儲並高度保密，除了條款和條件（為避免疑問，還包括隱私政策）的另有規定外。使用者使用服務時或之前，公司有必要收集使用者特定資訊，包括用戶名字、出生日期、聯繫方式以及使用者的行銷偏好（所有這些都應稱為「個人資訊」）。',
            '18.2 公司可能隨時讓用戶提供額外的細節，任何額外信息也將保密。',
            '18.3 應法律、政府部門或監管機構要求，公司有權在某些情況下向有關部門（例如調查洗錢、欺詐和其他犯罪活動的機構或政府）披露相關細節。'
          ]
        },
        {
          heading: '19. 網站 Cookies 的使用',
          body: [
            '19.1 客戶應被告知，個人資訊和資料將在註冊過程中通過 cookie 自動收集並存儲。資訊刪除或控制 cookie 可隱私政策中執行。'
          ]
        },
        {
          heading: '20. 無擔保',
          body: [
            '20.1 公司致力於為用戶提供合理技能和謹慎的服務。公司，無論是明示或暗示，對服務不做出保證或陳述。所有隱含的品質滿意、適用性、完整性或準確性的保證或條件特此排除在外。',
            '20.2 公司不保證以下：服務將滿足使用者需求或將完全不間斷、及時、安全無誤、糾正缺陷；或可用的網站或伺服器沒有病毒或漏洞；或網站功能完整、資訊準確可靠；或網站提供結果或任何資訊均準確。',
            '20.3 與亂數產生、投注、比賽結果或其他服務因素相關的系統或通信失誤，公司不負任何責任且有權視所有相關賽事（如適用）的投注無效。'
          ]
        },
        {
          heading: '21. 責任限制',
          body: [
            '21.1 獎金將被記入到用戶的帳戶或帳戶的損失，將在相關投注結果最終確定後反應到用戶的帳戶裡（對於體育賽事的情況，以體育管理機構的最終結果為准）。',
            '21.2 如果資金存入或從用戶的帳戶中扣除出現錯誤，使用者需承擔立即通知本公司的責任。由於記入使用者錯誤而產生的款項將被視為無效，必須歸還給本公司。使用者不得使用因為計入錯誤所產生的款項進行投注出售或投注，本公司保留將此類投注視為無效的權利。使用者在此同意歸還因為任何錯誤而被記入使用者帳戶的獎金將被撤回。',
            '21.3 本公司將不承擔任何由於印刷、技術或人為錯誤所造成的賠率錯誤。在錯誤的情況下，公司保留權利視投注在這些錯誤賠率的投注無效。',
            '21.4 本公司在任何情況下將被追究責任的公平、合同、侵權、疏忽，或以其他方式（或任何法律理論的任何損害或直接、間接或任何其他性質沒有限制，被認為或涉嫌造成的損失）來自或經本網站或它的內容包括造成的，但不限於，錯誤、不準確或本網站或其內容、故障、故障、延誤或操作或傳輸中斷之處，通訊線路故障，任何人，使用或本網站或它的內容，在內容的任何錯誤或遺漏，誤用的商業損失、損害、利潤損失、業務中斷、損失的商業資訊，或任何其他金錢或相應損失或損害（即使用戶已通知本公司該等損失或損害的可能性）。',
            '21.5 本公司不會對任何超出其合理控制的情況的條款和條件所造成的違約責任。',
            '21.6 本公司保留權利在任何時間撤回本網站及網站內容，並保留使用者存款超支追究的權利而不需事前通知用戶。'
          ]
        },
        {
          heading: '22. 賠償',
          body: [
            '22.1 用戶完全同意，不會由於所有索賠、債務、損害賠償、損失、成本和費用，包括律師費（不論其法律理論）所產生的有關條款和條件或規則和規例或任何因用戶的網站或任何其他人訪問本網站，及使用其他任何違約責任用戶的帳號資訊的細節的情況，對本公司其管理人員、董事、雇員、顧問、諮詢、代理和供應商造成任何損害。'
          ]
        },
        {
          heading: '23. 適用法律及司法管轄權',
          body: [
            '23.1 建造、有效性和履行本協議將受菲律賓的法律。然而，這並不妨礙攜帶任何有關任何禁止令或其他司法管轄區法院類似的救援行動的公司。本協定的英文版本將優先於任何其他語言的公司發行的版本。'
          ]
        },
        {
          heading: '24. 智慧財產權',
          body: [
            '24.1 網站的內容和服務受國版權和其他智慧財產權保護。權利的所有者為 {siteName} 或其他協力廠商許可者。',
            '24.2 除了在一定程度上須要使用服務下注或者押注外，未經公司事先書面同意不得以任何方式或形式，或包含在任何其他網站或任何公共或私人電子檢索系統或包括文本、圖形、視頻、資訊、代碼和/或軟體的服務再生、存儲、修改、複製、轉載、上傳、發佈、傳輸或散佈任何網站內容。',
            '24.3 嚴格禁止任何使用網站或服務或其内容於商業用途。'
          ]
        },
        {
          heading: '25. 負責任博彩',
          body: [
            '25.1 如須關於設備的任何資訊請與客戶團隊聯繫。',
            '25.2 公司將説明使用者使用合理的方式以確保使用者自我戒賭。然而，自我戒賭需要使用者和公司的共同努力。自我戒賭時用戶不能嘗試開設新帳戶，同時公司對用戶以下行為不負任何責任：用戶繼續賭博和/或試圖使用網站和/或服務，公司意識不到或不確定超出合理控制的情況自我戒賭，包括但不限於開設新帳戶或通過電話而不是在網上或使用不同的名稱或位址的情況。',
            '25.3 公司將嚴格履行以上承諾。'
          ]
        },
        {
          heading: '26. 聯繫我們',
          body: [
            '26.1 用戶可以通過客服尋求服務，聯繫的具體細節可以在網站上找到。請注意，所有電話都將被記錄並用於客戶支持團隊培訓和安全目的。'
          ]
        },
        {
          heading: '27. 定義',
          body: [
            '以下定義將在規則和條款中使用：',
            '使用者帳戶或帳戶：指公司允許客戶開設和使用的，被用來管理與網站和服務相關的資金支付的帳戶。',
            '投注：是指通過網站在任何賽事的結果和/或盤口上的投注、下注或賭注。',
            '公司：是指 {siteName}。',
            '內容：是指公司網站上提供任何媒介或形式的所有文字、信息、資料、軟體、可執行代碼、圖片、音頻或視頻材料。',
            '智慧財產權：指專利、發明權、版權和相關權利、道德權利、商標和服務商標、貿易名稱和功能變數名稱、商譽權或起訴假冒或不公平競爭權、設計權、電腦軟體權、數據庫權、機密信息（包括技術和商業機密）權和其他智慧財產權，無論是否註冊或未註冊的，包括所有申請（或有申請權）的、更新的或擴展的，以上權利和所有類似或相關權利或現在或將來在任何地方會存在的保護形式。',
            '隱私政策：是指通過隱私政策鏈接訪問的公司的隱私政策。',
            '規則：指的是適用於相關類型投注和/或網站博彩的投注和博彩規則。',
            '服務：指的是公司通過網站或軟體不時地提供的服務。',
            '軟體：是指用戶從公司不時地提供的網站下載或以其他方式使用的任何電腦程序。',
            '條款：是指 (a) 以上規則和條款；(b) 隱私政策；(c) 用戶使用的作用於服務上的合理的相關規則、獎金條款和任何額外條款。',
            '協力廠商：是指用戶必須接受的，作為訪問公司網站使用任何軟體，包括附錄中提及的 Playtech 終端使用者授權合同的任何額外的終端使用者的規則和條款。',
            '「公司」/「本公司」/「我們」：都指 {siteName}。',
            '網站功能變數名稱（{siteUrl}）：提供了公司的線上體育博彩服務，包括與包含這些功能變數名稱的所有頁面、所有使用這些功能變數名稱的運行或訪問的電腦程式、或/和所有使用這些功能變數名稱的材料和資料，包括但不限於文本、照片、圖形、視頻和音訊內容。'
          ]
        }
      ]
    }
  }
};

/**
 * 取得指定語系的條款文案；查無則 fallback 到 zh_TW。
 * @param {string} locale - convertLanguageCode(lng) 的結果，如 'zh_TW' / 'en_US'
 */
export const getTermsContent = (locale) => CONTENT[locale] || CONTENT.zh_TW;

export default CONTENT;
